import { LotameLightningTag } from "./LotameLightningTag";
import { LotameLightningTagData } from "./LotameLightningTagData";
import { insertScriptToDocument } from "../utils/HtmlScripts";
import { DataSyncLibEventNames } from "../shared/DataSyncLibEventNames";
import { LotameConfig } from "./LotameConfig";
import { inject, injectable, singleton } from "tsyringe";
import { LotameProfile } from "./LotameProfile";
import { UserIdExchange } from "../shared/UserIdExchange";
import { Logger } from "../utils/Logger";
import { validateLotameCookie } from "../utils/Cookies";

@injectable()
@singleton()
export class LotameClient {
  constructor(
    @inject(LotameConfig) private lotameConfig: LotameConfig,
    @inject(UserIdExchange) private userIdExchange: UserIdExchange,
  ) {}

  private isProfileReady = false;

  /**
   * Register a function to invoke once the LotameService has been initialized.
   * If Lotame's profile is already loaded, the callback will be invoked immediately.
   * @param callback The function to invoke
   */
  public invokeWhenLotameProfileReady(callback: () => void): void {
    if (this.isProfileReady) {
      callback();
    } else {
      window.addEventListener(DataSyncLibEventNames.LotameServiceInitialized, callback);
    }
  }

  /**
   * Register a callback to invoke each time Lotame's profile is updated
   * (should be once per page load & view)
   * @param callback A callback to invoke each time the event is triggered.
   */
  public invokeWhenLotameProfileUpdated(callback: () => void): void {
    window.addEventListener(DataSyncLibEventNames.LotameProfileUpdated, callback);
  }

  public callLotameApiCollect(data: LotameLightningTagData): void {
    const lotameApi = this.getLotameApi();

    if (lotameApi) {
      lotameApi.data.behaviors = undefined;
      lotameApi.cmd.push(function () {
        lotameApi.collect(data);
      });
    } else {
      Logger.error("LotameApi not available.");
    }
  }

  public lotameLightningTagSetup(): void {
    // Reference for this implementation https://my.lotame.com/t/g9hxvnw/detailed-reference-guide#data-object
    // The IDE may mistakenly think that the onProfileReady is unused, but Lotame's library is using it.
    // noinspection JSUnusedGlobalSymbols
    const lotameTagInput = {
      data: [],
      config: {
        clientId: Number(this.lotameConfig.clientId),
        audienceLocalStorage: this.lotameConfig.audienceLocalStorageKey,

        // Take note: this callback is invoked on the first page load AND on later page views.
        onProfileReady: async (profile: LotameProfile): Promise<void> => {
          if (!this.isProfileReady) {
            validateLotameCookie();
            this.isProfileReady = true;
            window.dispatchEvent(new Event(DataSyncLibEventNames.LotameServiceInitialized));
          }
          window.dispatchEvent(new Event(DataSyncLibEventNames.LotameProfileUpdated));
          await this.userIdExchange.setLotameIdInUie(profile.getProfileId());
        },
      },
    };

    // Lotame initialization
    const lotameConfig = lotameTagInput.config;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const namespace = ((window as any)[`lotame_${this.lotameConfig.clientId}`] = {
      config: {},
      data: {},
      cmd: [],
    });
    namespace.config = lotameConfig;
    namespace.data = lotameTagInput.data || {};
    namespace.cmd = namespace.cmd || [];
    const src = `https://tags.crwdcntrl.net/lt/c/${this.lotameConfig.clientId}/lt.min.js`;
    insertScriptToDocument(document, src);
  }

  /**
   * Fetch the current list of audiences from the local storage
   * by using Lotame's API.
   * @param limit
   */
  public getAudiences(limit?: number): string[] {
    const lotameApi = this.getLotameApi();
    if (!lotameApi) {
      Logger.error("LotameAPI not initialized. Could not fetch audiences.");
      return [];
    }
    return lotameApi.getAudiences(limit);
  }

  /**
   * Fetch the current list of audiences from the local storage
   * without using Lotame's API.
   */
  public getAudiencesFromLocalStorage(): string[] {
    const audiences = localStorage.getItem(this.lotameConfig.audienceLocalStorageKey);
    if (!audiences) {
      return [];
    }
    return audiences.split(",");
  }

  /**
   * Communicate a new page view event to Lotame.
   * Will trigger the callback registered on the onProfileReady property
   * of the configuration object.
   */
  public triggerPageView(): void {
    const lotameApi = this.getLotameApi();
    if (lotameApi) {
      lotameApi.cmd.push(function () {
        lotameApi.page();
      });
    }
  }

  private getLotameApi(): LotameLightningTag | undefined {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any)[`lotame_${this.lotameConfig.clientId}`];
  }
}
